import type {
  Appointment,
  AppointmentActivity,
  AppointmentBookedBy,
  AppointmentPatient,
  AppointmentStatus,
} from "@/features/clinic/types/appointment";
import type { Doctor } from "@/features/clinic/types/doctor";
import type { Treatment } from "@/features/clinic/types/treatment";
import type { Contact } from "@/features/contact/types";

import { DateTime } from "luxon";

import { TUTORIAL_CONTACT_COUNT } from "../config";

import { campaignForIndex } from "./campaign-attribution";
import {
  createId,
  createPastTimestamp,
  pickRandom,
  randomBool,
  randomInt,
} from "./base";

import {
  APPOINTMENT_ACTIVITY_ACTION,
  APPOINTMENT_BOOKED_BY,
  BOOKING_TYPE,
  VISIT_TYPE,
} from "@/features/clinic/types/appointment";

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  pending: "#3b82f6",
  confirmed: "#22c55e",
  completed: "#6b7280",
  cancelled: "#ef4444",
  no_show: "#f59e0b",
};

interface GenerateAppointmentsConfig {
  organizationId: string;
  workspaceId: string;
  doctors: Doctor[];
  treatments: Treatment[];
  contacts: Contact[];
  userIds?: string[];
  daysRange?: number;
  appointmentsPerDay?: number;
}

const BOOKED_BY_OPTIONS: AppointmentBookedBy[] = [
  APPOINTMENT_BOOKED_BY.USER,
  APPOINTMENT_BOOKED_BY.CRM,
  APPOINTMENT_BOOKED_BY.AI,
];

const VISIT_TYPE_OPTIONS = Object.values(VISIT_TYPE);

function generateMockActivities(
  createdAt: string,
  status: AppointmentStatus,
  doctor: Doctor,
  treatment: { id: string; name: string } | null,
  bookedBy: AppointmentBookedBy,
  userIds: string[],
  allDoctors: Doctor[],
  allTreatments: { id: string; name: string }[],
): AppointmentActivity[] {
  const activities: AppointmentActivity[] = [];
  const createdDate = DateTime.fromISO(createdAt);

  // Always add CREATE activity
  const createUserId =
    bookedBy === APPOINTMENT_BOOKED_BY.USER && userIds.length > 0
      ? pickRandom(userIds)
      : null;

  activities.push({
    userId: createUserId,
    integrationId: null,
    action: APPOINTMENT_ACTIVITY_ACTION.CREATE,
    timestamp: createdAt,
  });

  // Maybe add status update activities
  if (status !== "pending" && randomBool(0.7)) {
    const statusUpdateTime = createdDate
      .plus({ hours: randomInt(1, 48) })
      .toISO();

    if (status === "confirmed") {
      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: statusUpdateTime ?? createdAt,
        payload: { previous: "pending", new: "confirmed" },
      });
    } else if (status === "completed") {
      // First confirm, then complete
      const confirmTime = createdDate.plus({ hours: randomInt(1, 24) }).toISO();
      const completeTime = createdDate
        .plus({ hours: randomInt(48, 96) })
        .toISO();

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: confirmTime ?? createdAt,
        payload: { previous: "pending", new: "confirmed" },
      });

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: completeTime ?? createdAt,
        payload: { previous: "confirmed", new: "completed" },
      });
    } else if (status === "cancelled") {
      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: statusUpdateTime ?? createdAt,
        payload: { previous: "pending", new: "cancelled" },
      });
    } else if (status === "no_show") {
      const confirmTime = createdDate.plus({ hours: randomInt(1, 24) }).toISO();
      const noShowTime = createdDate.plus({ hours: randomInt(48, 96) }).toISO();

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: confirmTime ?? createdAt,
        payload: { previous: "pending", new: "confirmed" },
      });

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.STATUS_UPDATE,
        timestamp: noShowTime ?? createdAt,
        payload: { previous: "confirmed", new: "no_show" },
      });
    }
  }

  // Maybe add doctor update activity
  if (randomBool(0.2) && allDoctors.length > 1) {
    const otherDoctors = allDoctors.filter((d) => d.id !== doctor.id);

    if (otherDoctors.length > 0) {
      const previousDoctor = pickRandom(otherDoctors);
      const doctorUpdateTime = createdDate
        .plus({ hours: randomInt(2, 24) })
        .toISO();

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.DOCTOR_UPDATE,
        timestamp: doctorUpdateTime ?? createdAt,
        payload: { previous: previousDoctor.id, new: doctor.id },
      });
    }
  }

  // Maybe add treatment update activity
  if (treatment && randomBool(0.15) && allTreatments.length > 1) {
    const otherTreatments = allTreatments.filter((t) => t.id !== treatment.id);

    if (otherTreatments.length > 0) {
      const previousTreatment = pickRandom(otherTreatments);
      const treatmentUpdateTime = createdDate
        .plus({ hours: randomInt(2, 24) })
        .toISO();

      activities.push({
        userId: userIds.length > 0 ? pickRandom(userIds) : null,
        integrationId: null,
        action: APPOINTMENT_ACTIVITY_ACTION.TREATMENT_UPDATE,
        timestamp: treatmentUpdateTime ?? createdAt,
        payload: { previous: previousTreatment.id, new: treatment.id },
      });
    }
  }

  return activities;
}

/**
 * A booking the CRM refused, so the "Not in the CRM" worklist and the alert
 * that links to it both open with something in them. Every seventh booking made
 * on our side, and only inside the window that queue can still see.
 */
const crmPushFailureFor = (
  appointment: Appointment,
  index: number,
): Pick<Appointment, "externalId" | "externalData"> | Record<string, never> => {
  const isRecent =
    DateTime.fromISO(appointment.startDate) >=
    DateTime.now().minus({ days: 25 });

  if (
    index % 7 !== 3 ||
    !isRecent ||
    appointment.bookedBy === APPOINTMENT_BOOKED_BY.CRM ||
    appointment.status === "cancelled"
  ) {
    return {};
  }

  return {
    externalId: null,
    externalData: {
      error: {
        code: "PATIENT_NOT_FOUND",
        action: "create",
        message: "No matching patient on the CRM - add the MRN and retry.",
        occurredAt: appointment.createdAt,
      },
    },
  };
};

export function generateAppointments({
  organizationId,
  workspaceId,
  doctors,
  treatments,
  contacts,
  userIds = [],
  daysRange = 14,
  appointmentsPerDay = 4,
}: GenerateAppointmentsConfig): Appointment[] {
  const appointments: Appointment[] = [];
  const todayStart = DateTime.now().startOf("day");

  for (const doctor of doctors) {
    const doctorTreatments = treatments.filter((t) =>
      doctor.treatments?.some((dt) => dt.id === t.id),
    );

    for (let dayOffset = -3; dayOffset <= daysRange; dayOffset++) {
      const date = todayStart.plus({ days: dayOffset });

      // Skip weekends (luxon: 6 = Saturday, 7 = Sunday)
      if (date.weekday === 6 || date.weekday === 7) continue;

      const numAppointments = randomInt(0, appointmentsPerDay * 2);
      const usedSlots: { start: number; end: number }[] = [];

      for (let i = 0; i < numAppointments; i++) {
        const startHour = randomInt(9, 17);
        const startMinute = randomBool() ? 0 : 30;
        const duration = randomInt(1, 4) * 30;

        const startTime = startHour * 60 + startMinute;
        const endTime = startTime + duration;

        if (endTime > 18 * 60) continue;

        const hasOverlap = usedSlots.some(
          (slot) => startTime < slot.end && endTime > slot.start,
        );

        if (hasOverlap) continue;

        usedSlots.push({ start: startTime, end: endTime });

        const startDate = date.set({
          hour: startHour,
          minute: startMinute,
          second: 0,
        });
        const endDate = startDate.plus({ minutes: duration });

        let status: AppointmentStatus;

        if (dayOffset < 0) {
          status = pickRandom(["completed", "cancelled", "no_show"]);
        } else if (dayOffset === 0) {
          status = pickRandom(["pending", "confirmed", "completed"]);
        } else {
          status = pickRandom(["pending", "confirmed"]);
        }

        const contact = randomBool(0.9) ? pickRandom(contacts) : null;
        // The tutorial contacts (the "My inbox" threads) must never carry an
        // unresolved appointment: no stale status today and no unpriced service
        // visit, so their conversations open without an attention banner.
        const keepClean =
          !!contact &&
          dayOffset <= 0 &&
          contacts
            .slice(0, TUTORIAL_CONTACT_COUNT)
            .some((c) => c.id === contact.id);

        if (keepClean && status !== "completed" && endDate < DateTime.now()) {
          status = "completed";
        }
        const treatment =
          doctorTreatments.length > 0 && randomBool(0.8)
            ? pickRandom(doctorTreatments)
            : null;
        const visitType = randomBool(0.4)
          ? pickRandom(VISIT_TYPE_OPTIONS)
          : null;

        const timestamp = createPastTimestamp(daysRange + 7);

        // Generate patients - sometimes from contact, sometimes additional
        const patients: AppointmentPatient[] = [];

        if (contact && randomBool(0.7)) {
          patients.push({
            firstName: contact.firstName || "",
            lastName: contact.lastName || "",
            phone: contact.phone || "",
            contactId: contact.id,
          });
        }
        // Add additional patients randomly (for group appointments)
        if (randomBool(0.2)) {
          const additionalContact = pickRandom(contacts);

          if (additionalContact && additionalContact.id !== contact?.id) {
            patients.push({
              firstName: additionalContact.firstName || "",
              lastName: additionalContact.lastName || "",
              phone: additionalContact.phone || "",
              contactId: additionalContact.id,
            });
          }
        }

        // Generate bookedBy and bookedByUserId
        const bookedBy = pickRandom(BOOKED_BY_OPTIONS);
        const bookedByUserId =
          bookedBy === APPOINTMENT_BOOKED_BY.USER && userIds.length > 0
            ? pickRandom(userIds)
            : null;

        // Generate activities
        const treatmentForActivities = treatment
          ? { id: treatment.id, name: treatment.name }
          : null;
        const allTreatmentsForActivities = treatments.map((t) => ({
          id: t.id,
          name: t.name,
        }));
        const activities = generateMockActivities(
          timestamp,
          status,
          doctor,
          treatmentForActivities,
          bookedBy,
          userIds,
          doctors,
          allTreatmentsForActivities,
        );

        appointments.push({
          id: createId(),
          startDate: startDate.toISO() ?? "",
          endDate: endDate.toISO() ?? "",
          doctorId: doctor.id,
          doctor: {
            id: doctor.id,
            name: doctor.name,
            photo: doctor.photo,
            externalId: doctor.externalId ?? null,
          },
          contactId: contact?.id ?? null,
          contact: contact
            ? {
                id: contact.id,
                firstName: contact.firstName,
                lastName: contact.lastName,
                phone: contact.phone,
                email: contact.email,
                imageUrl: contact.imageUrl,
                assignedUserId: contact.assignedUserId,
                origin: contact.origin,
              }
            : undefined,
          treatmentId: treatment?.id ?? null,
          treatment: treatment
            ? { id: treatment.id, name: treatment.name }
            : null,
          patients,
          status,
          // The mock viewer is an admin: null means "visible but unpriced",
          // matching the API's omit-unless-admin contract for `value`.
          value: null,
          notes: randomBool(0.3)
            ? "Patient requested early morning slot"
            : null,
          visitType,
          // Set per appointment, not per treatment: a free consultation *for* a
          // hair transplant and the transplant itself share a treatment, and
          // only whoever booked it knows which this is.
          bookingType:
            keepClean || randomBool(0.35)
              ? BOOKING_TYPE.CONSULTATION
              : BOOKING_TYPE.SERVICE,
          // Unanswered on purpose, same reason as the outcome below: the
          // payment worklist needs something to chase on open.
          isPaid: null,
          // Left unanswered on purpose: a completed consultation with no outcome
          // is the state the whole flow exists to clear, so dev:mock opens with
          // a worklist that has something in it.
          outcome: null,
          isResolveBlocked: false,
          resolveBlockedReason: null,
          originAppointmentId: null,
          color: STATUS_COLORS[status],
          organizationId,
          workspaceId,
          createdAt: timestamp,
          updatedAt: timestamp,
          activities,
          bookedBy,
          bookedByUserId,
        });
      }
    }
  }

  // Stamped after the sort: the campaign report mock attributes by store
  // index, so the pattern has to be applied to the same ordering it reads.
  return appointments
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )
    .map((appointment, index) => {
      const campaign = campaignForIndex(index);

      return {
        ...appointment,
        campaignId: campaign?.campaignId ?? null,
        campaignName: campaign?.campaignName ?? null,
        ...crmPushFailureFor(appointment, index),
      };
    });
}
